import { createContext, type ReactNode, useContext } from "react";

/**
 * Context published by the `/calendar` page (`Calendar.tsx`) for behavior its
 * children need that only the page can provide.
 *
 * Null when not on the calendar page; consumers fall back to their default
 * behavior in that case.
 */
export interface CalendarPageHandlers {
  /**
   * "Send in background" landing: the user fired a task off and we deliberately
   * keep them where they are instead of opening the new thread. Always shows a
   * confirmation toast; if the threads list is already visible on the left, also
   * flashes the new row so the user sees where it landed. Used by the
   * agent-trigger popovers and "Create or ask" dialog, per the
   * `sendInBackground` preference.
   */
  notifyThreadSentInBackground: (threadId: number) => void;
  /**
   * Open a thread the way this page opens them: on the canvas beside the list,
   * with the calendar named in the header as the way back. Used by the
   * agent-trigger popovers, which start a thread from a chip on the grid — the
   * default landing (the thread's own page) takes the sidebar and the canvas
   * with it, which is a strange place to arrive from a click on an event.
   *
   * Mobile has one column, so there it still lands on the thread's page. That
   * is the page's own decision, not the caller's.
   */
  openThread: (threadId: number) => void;
}

const CalendarPageContext = createContext<CalendarPageHandlers | null>(null);

export const CalendarPageProvider = ({
  value,
  children,
}: {
  value: CalendarPageHandlers;
  children: ReactNode;
}) => {
  return <CalendarPageContext.Provider value={value}>{children}</CalendarPageContext.Provider>;
};

/**
 * Consume the calendar-page context. Returns `null` when not rendered under
 * a `CalendarPageProvider` (i.e. outside the `/calendar` page).
 */
export const useCalendarPage = (): CalendarPageHandlers | null => {
  return useContext(CalendarPageContext);
};
